import React, { useEffect, useReducer, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '@material-ui/core/CircularProgress';
import Collapse from '@material-ui/core/Collapse';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import _get from 'lodash/get';
import useDataSource, { dataSourceTypes } from 'Common/Utils/Hooks/useDataSource';
import { hasValue, convertToDate, formatDate, makeStyles } from 'utils';
import classes from './table.module.css';
import { makeKeyGetter } from 'Common/Utils/dataSourceHelpers';

const actions = {
    SET: 'SET',
    PAGE: 'PAGE',
    PAGE_SIZE: 'PAGE_SIZE',
};
const pageSizeOptions = [5, 10, 25, 100];
const styles = makeStyles(classes);

function DataTable({
    dataSource,
    keyField,
    expansion,
    pagination,
    schema,
    selection,
    selected,
    summary,
    variant,
    onCellChange,
    onCellClick,
    onError,
    onLoad,
    onSelectionChange,
    componentProps
}) {
    const [{ data: rows, totalCount, isLoading }, setDataSource] = useDataSource(null, onError, onLoad);
    const [{ page, pageSize }, dispatch] = useReducer(tableReducer, { setDataSource }, initTableState);
    const cols = useMemo(() => buildColumns(schema), [schema]);
    const keyGetter = useMemo(() => makeKeyGetter(keyField), [keyField]);
    const variantInner = variant === 'inner';

    const handleRowClick = useCallback((_, row) => {
        if (!hasButtonSelectionType())
        {
            onSelectionChange && onSelectionChange(row);
        }
    }, [onSelectionChange]);

    const hasButtonSelectionType = () => {
        return cols.some(r => r.type === 'button');
    }

    const handleButtonClick = useCallback((_, row) => {
        onSelectionChange && onSelectionChange(row);
    }, [onSelectionChange]);

    const handlePageChange = useCallback((_, value) => {
        dispatch({ type: actions.PAGE, payload: value });
    }, []);

    const handlePageSizeChange = useCallback((e) => {
        dispatch({ type: actions.PAGE_SIZE, payload: e.target.value });
    }, []);

    useEffect(() => {
        dispatch({ type: actions.SET, payload: dataSource });
    }, [dataSource, dispatch]);

    return (
        <div className={styles('data-table', variantInner && 'data-table--inner')}>
            <TableContainer>
                <Table stickyHeader size={variantInner ? 'small' : undefined}>
                    <TableHead>
                        <TableRow>
                            {cols.map(col => (
                                <TableCell
                                    key={col.field}
                                    align={col.align}
                                    className={styles(col.className)}
                                    >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading && (
                            <TableRow key={0}>
                                <TableCell colSpan={cols.length} align="center">
                                    <CircularProgress color="inherit" size={14} />&nbsp;Loading...
                                </TableCell>
                            </TableRow>
                        )}

                        {!isLoading && rows && applyPagination(rows, pagination, page, pageSize).map(row => {
                            const keyValue = keyGetter(row);

                            return (
                                <Row
                                    key={keyValue}
                                    keyValue={keyValue}
                                    expansion={expansion}
                                    row={row}
                                    cols={cols}
                                    selected={selection && selected && isMatch(selected, row, keyGetter)}
                                    selection={selection}
                                    onCellChange={onCellChange}
                                    onCellClick={onCellClick}
                                    onRowClick={handleRowClick}
                                    onButtonClick={handleButtonClick}
                                    componentProps={componentProps}
                                    hasButtonSelectionType={hasButtonSelectionType()}
                                />
                            );    
                        })}

                        {!isLoading && rows && typeof(summary) === 'function' && (
                            <TableRow className={styles('data-table__row--summary')}>
                                {summary({ data: rows }) }
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {pagination && !isLoading && (
                <TablePagination
                    rowsPerPageOptions={pageSizeOptions}
                    component="div"
                    count={totalCount}
                    rowsPerPage={pageSize}
                    page={page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePageSizeChange}
                />
            )}
        </div>
    );
}

const Row = ({
    keyValue,
    row,
    cols,
    expansion: Expansion,
    selected,
    selection,
    onRowClick,
    onCellChange,
    onCellClick,
    onButtonClick,
    componentProps,
    hasButtonSelectionType
}) => {
    return (
        <>
            <TableRow
                className={styles(!hasButtonSelectionType && selected && 'selected-row', !hasButtonSelectionType && selection && 'hover-row')}
                hover={selection}
                selected={selected}
                onClick={e => onRowClick(e, row)}
                >
                 {cols.map(col => (
                    <Cell
                        key={col.field}
                        col={col}
                        row={row}
                        keyValue={keyValue}
                        selected={selected}
                        onCellChange={onCellChange}
                        onCellClick={onCellClick}
                        onButtonClick={onButtonClick}
                        componentProps={componentProps}
                    />
                ))}
            </TableRow>
            { Expansion && selected && (
                <TableRow selected className={styles('selected-row')}>
                    <TableCell
                        key={1}
                        className={styles('data-table__expansion-cell')}
                        colSpan={cols.length}
                        selected>
                        <Collapse in={true} timeout="auto" unmountOnExit>
                            <div className={styles('data-table__expansion')}>
                                <Expansion row={row} />
                            </div>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

const Cell = props => {
    const { col, row, onButtonClick } = props;
    const Content = col.component || CellContent;
    const { componentProps = {}, ...cellProps } = props;
    const { field } = col;
    const { commonProps = {}, [field]: fieldProps = {} } = componentProps;

    if (col.type === 'button')
    {
        return (
            <TableCell align={col.align} className={styles(col.className)}>
                <button
                    aria-label={col.arialabel}
                    className="btn btn-primary btn-sm"
                    style={{ width: '130px' }}
                    onClick={e => onButtonClick(e, row)}
                >{col.label}</button>
            </TableCell>
        );    
    }
    else
    {
        return (
            <TableCell align={col.align} className={styles(col.className)}>
                <Content {...cellProps} {...commonProps} {...fieldProps} />
            </TableCell>
        );    
    }
};

const CellContent = ({ row, col }) => {
    const value = col.calculate ? col.calculate(row) : _get(row, col.field);
    const formattedValue = formatValue(value, col);
    return <>{formattedValue}</>;
};

function applyPagination(rows, pagination, page, pageSize) {
    if (pagination && rows && rows.length && rows.length > pageSize) {
        const start = Math.max(0, page * pageSize);
        const end = Math.max(start, start + pageSize);

        if (start < rows.length) {
            return rows.slice(start, end);
        }
    }

    return rows;
}

function buildColumns(schema) {
    return schema
        .filter(col => col.forDisplay !== false)
        .map(col => ({
            ...col,
            align: col.align || (col.type === 'date' || col.type === 'number' ? 'right' : 'left'),
        }));
}

function initTableState({ setDataSource }) {
    return {
        setDataSource,
        dataSource: null,
        page: 0,
        pageSize: 10
    };
}

function isMatch(selected, row, keyGetter) {
    return selected === row || (keyGetter(selected) === keyGetter(row));
}

function formatValue(value, column) {
    if (typeof(column.format) === 'function') {
        return column.format(value, column, formatValueBase);
    }

    return formatValueBase(value, column);
}

function formatValueBase(value, column) {
    if (!hasValue(value)) {
        return '';
    }

    switch (column.type) {
        case 'date':
            return formatDate(convertToDate(value));

        default:
            return value;
    }
}

function tableReducer(state, action) {
    switch (action.type) {
        case actions.SET:
            const dataSource = action.payload;
            if (typeof(dataSource) === 'function') {
                state.setDataSource(dataSource(1, state.pageSize));
            } else {
                state.setDataSource(dataSource);
            }
            return {
                ...state,
                page: 0,
                dataSource
            };

        case actions.PAGE:
            if (typeof(state.dataSource) === 'function') {
                state.setDataSource(state.dataSource(action.payload + 1, state.pageSize));
            }
            return {
                ...state,
                page: action.payload,
            };

        case actions.PAGE_SIZE:
            if (typeof(state.dataSource) === 'function') {
                state.setDataSource(state.dataSource(1, action.payload));
            }
            return {
                ...state,
                page: 0,
                pageSize: action.payload
            };

        default:
            return state;
    }
}

const schemaShape = {
    calculate: PropTypes.func,
    className: PropTypes.string,
    component: PropTypes.elementType,
    field: PropTypes.string.isRequired,
    forDisplay: PropTypes.bool,
    forExport: PropTypes.bool,
    format: PropTypes.func,
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['date', 'number', 'button']),
};

const keyFieldTypes = [
    PropTypes.string,
    PropTypes.func
];

DataTable.propTypes = {
    dataSource: PropTypes.oneOfType(dataSourceTypes),
    keyField: PropTypes.oneOfType(keyFieldTypes).isRequired,
    pagination: PropTypes.bool,
    schema: PropTypes.arrayOf(PropTypes.shape(schemaShape)).isRequired,
    selection: PropTypes.bool,
    selected: PropTypes.any,
    summary: PropTypes.func,
    onCellChange: PropTypes.func,
    onCellClick: PropTypes.func,
    onError: PropTypes.func,
    onLoad: PropTypes.func,
    onSelectionChange: PropTypes.func,
    componentProps: PropTypes.object
};

export default DataTable;
export { dataSourceTypes, keyFieldTypes, schemaShape };